import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { map, switchMap } from 'rxjs';
import {
  CampaignReportDetailsTableComponent,
  ReportDetailsTableOptions,
  TArrangementSort,
} from '../../../../../../../components/tables/campaign-report-details-table/campaign-report-details-table.component';
import { CampaignsService } from '../../../../../../../services/campaign/campaigns-api.service';
import {
  CampaignInsightsService,
  DeliveryFilter,
} from '../../../../../../../services/campaign/campaign-insights.service';
import { CampaignDetails, MessagesReport } from '../../../../types/campaignDetails';
import { formatWhen, toDate } from '../../../../../../../../utils/sms';
import { ReceiptTable } from '../receipt-table';
import { orderedMessages, withMeta } from '../../../campaign-sends';

/** Every recipient of one campaign message, across all of its sends. */
@Component({
  selector: 'app-report-message-contact-details',
  standalone: true,
  imports: [RouterLink, CampaignReportDetailsTableComponent],
  templateUrl: './report-message-contact-details.component.html',
  styleUrl: './report-message-contact-details.component.css',
})
export class ReportMessageContactDetailsComponent implements OnInit {
  route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private insights = inject(CampaignInsightsService);
  messageId!: string;
  campaignsService = inject(CampaignsService);
  messagesReport: MessagesReport | null = null;
  scheduleOptions: ReportDetailsTableOptions[] = [{ name: 'All sends', id: '' }];
  subject = '';
  arrangementSort: TArrangementSort = '';
  backLink: (string | number)[] = [];

  table = new ReceiptTable(this.insights, this.destroyRef, () => this.messageId);

  updateSearchTerm(term: string) {
    this.table.setSearch(term);
  }

  updateSortArrangementCriteria(criteria: TArrangementSort) {
    this.arrangementSort = criteria;
    this.table.setSort(criteria);
  }

  updateMessageSchedule(uuid: string) {
    this.table.setSchedule(uuid);
  }

  updateStatusFilter(filter: DeliveryFilter) {
    this.table.setFilter(filter);
  }

  downloadCsv() {
    this.table.download('campaign message', this.subject || this.messagesReport?.message, 'recipients');
  }

  ngOnInit(): void {
    this.messageId = this.route.parent?.snapshot.paramMap.get('messageId')!;
    const campaignUuid = this.campaignsService.campaignUuid;
    const idRoute = this.route.snapshot.pathFromRoot.find((r) => r.paramMap.has('id'));
    this.backLink = [
      '/organization/campaigns',
      idRoute?.paramMap.get('id') ?? '',
      campaignUuid,
      'reports',
      this.messageId,
      'message-report',
    ];

    // The route carries the message uuid, which only the message's own GET has.
    this.campaignsService.campaignDetails
      .pipe(
        switchMap((data) => {
          const messages = orderedMessages(data as CampaignDetails);
          return this.insights
            .messageMeta(messages.map((m) => m.id))
            .pipe(map((meta) => messages.map((m) => withMeta(m, meta[m.id]))));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((messages) => {
        const message = messages.find((m) => !!m.uuid && m.uuid === this.messageId);
        this.subject = String(message?.messageSubject ?? '').trim();
      });

    // Every recipient of the message, as before; a send can then narrow it.
    this.table.load();

    if (campaignUuid) {
      this.insights
        .messageReports(campaignUuid)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (reports) => {
            this.messagesReport = reports.find((r) => r?.referenceId == this.messageId) ?? null;
            const sends = [...(this.messagesReport?.reports ?? [])]
              .filter((send) => !!send?.scheduleUuid)
              .sort(
                (a, b) =>
                  (toDate(a.messageSchedules ?? null)?.getTime() ?? 0) -
                  (toDate(b.messageSchedules ?? null)?.getTime() ?? 0)
              );
            this.scheduleOptions = [
              { name: 'All sends', id: '' },
              ...sends.map((send) => {
                const at = toDate(send.messageSchedules ?? null);
                return { name: at ? formatWhen(at) : 'No send time', id: send.scheduleUuid };
              }),
            ];
          },
          error: () => {
            // The table still lists every recipient; only the send picker is missing.
          },
        });
    }
  }
}
